import React, { useEffect, useState, useContext } from 'react';
import axios from './axiosInstance';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import { exportBrandedPdf } from './utils/brandedPdf';
import './Pages.css';
import Pagination from './components/Pagination';
import { UserContext } from './UserContext';
import { formatDateTime } from './utils/dateFormat';

function AuditTrail() {
  const { user } = useContext(UserContext);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    // Latest 200 entries (the API's maximum page) — filtered to your organisation
    // unless you are a system admin.
    axios.get('/audit/trails?limit=200')
      .then(response => {
        setLogs(response.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const handleSort = (col) => {
    const order = sortColumn === col && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortColumn(col);
    setSortOrder(order);
  };

  const filtered = searchTerm
    ? logs.filter(log =>
        Object.values(log)
          .join(' ')
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
    : logs;

  const sorted = filtered.sort((a, b) => {
    const valA = (a[sortColumn] || '').toString().toLowerCase();
    const valB = (b[sortColumn] || '').toString().toLowerCase();
    return valA < valB ? (sortOrder === 'asc' ? -1 : 1) : valA > valB ? (sortOrder === 'asc' ? 1 : -1) : 0;
  });

  const totalPages = Math.ceil(sorted.length / itemsPerPage);
  const currentLogs = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(logs.map(log => ({
      Action: log.action_type,
      Module: log.module_name,
      Target: log.target,
      Summary: log.result_summary,
      Status: log.status,
      User: log.user_email || log.user_id || 'N/A',
      IP: log.ip_address,
      Time: formatDateTime(log.timestamp)
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Audit Logs');
    XLSX.writeFile(wb, 'audit_logs.xlsx');
  };

  const exportPDF = () => exportBrandedPdf({
    title: 'AUDIT TRAIL — REPORT',
    user,
    searchValue: 'All audit logs',
    fileName: 'Audit_Trail_Report',
    columns: [
      { header: 'Action', width: 12, bold: true }, { header: 'Module', width: 11 }, { header: 'Target', width: 16 },
      { header: 'Summary', width: 26 }, { header: 'Status', width: 7 }, { header: 'User', width: 14 },
      { header: 'IP', width: 10 }, { header: 'Time', width: 12 }
    ],
    rows: logs.map(log => [
      log.action_type, log.module_name, log.target, log.result_summary, log.status,
      log.user_email || log.user_id || 'N/A', log.ip_address, formatDateTime(log.timestamp)
    ])
  });

  if (loading) return <div className="page-container">Loading Audit Logs...</div>;
  if (error) return <div className="page-container">Error: {error}</div>;


  return (
    <div className="page-container">
      <div className="table-controls">
        <input
          type="text"
          placeholder="Search logs..."
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
        <div className="button-group">
          <CSVLink
            data={logs.map(log => ({
              Action: log.action_type,
              Module: log.module_name,
              Target: log.target,
              Summary: log.result_summary,
              Status: log.status,
              User: log.user_email || log.user_id || 'N/A',
              IP: log.ip_address,
              Time: formatDateTime(log.timestamp)
            }))}
            filename="audit_logs.csv"
            className="export-button"
          >
            Export CSV
          </CSVLink>
          <button className="export-button" onClick={exportExcel}>Export Excel</button>
          <button className="export-button" onClick={exportPDF}>Export PDF</button>
        </div>
      </div>

      <div style={{ marginBottom: '1rem', fontWeight: 'bold', fontSize: '1rem' }}>
        Showing {currentLogs.length} of {filtered.length} logs {searchTerm ? `(filtered from ${logs.length})` : ''}
      </div>

      <div className="table-container">
        <table className="pips-table">
          <thead>
            <tr>
              <th>#</th>
              <th onClick={() => handleSort('action_type')}>Action</th>
              <th onClick={() => handleSort('module_name')}>Module</th>
              <th onClick={() => handleSort('target')}>Target</th>
              <th onClick={() => handleSort('result_summary')}>Summary</th>
              <th onClick={() => handleSort('status')}>Status</th>
              <th>User</th>
              <th>IP</th>
              <th onClick={() => handleSort('timestamp')}>
                Time {sortColumn === 'timestamp' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
              </th>
            </tr>
          </thead>
          <tbody>
            {currentLogs.map((log, index) => (
              <tr key={log.id}>
                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                <td>{log.action_type}</td>
                <td>{log.module_name}</td>
                <td>{log.target}</td>
                <td>{log.result_summary}</td>
                <td>{log.status}</td>
                <td>{log.user_email || log.user_id || 'N/A'}</td>
                <td>{log.ip_address}</td>
                <td>{formatDateTime(log.timestamp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={sorted.length}
        itemsPerPage={itemsPerPage}
        itemLabel="log entry"
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
      />
    </div>
  );
}

export default AuditTrail;
